<script setup>
import { ref, computed, onMounted } from 'vue';
import {
  useMapGetter,
  useFunctionGetter,
  useStore,
} from 'dashboard/composables/store';
import SettingIntroBanner from 'dashboard/components/widgets/SettingIntroBanner.vue';
import AssistantConfiguration from './components/AssistantConfiguration.vue';
import AssistantTester from './components/AssistantTester.vue';
import InstructionsPage from './settingsPage/InstructionsPage.vue';
import KnowledgePage from './settingsPage/KnowledgePage.vue';
import ProcedureEditor from './settingsPage/ProcedureEditor.vue';
import AssistantTesterMobile from './components/AssistantTesterMobile.vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const { t } = useI18n();
const selectedTabIndex = ref(0);

const assistants = useMapGetter('assistants/getAssistants');
const inboxes = useMapGetter('inboxes/getInboxes');
const assistantUIFlags = useMapGetter('assistants/getUIFlags');
const inboxUIFlags = useMapGetter('inboxes/getUIFlags');
const store = useStore();
const route = useRoute();

const tabs = computed(() => {
  let configuredTabs = [
    {
      key: 'assistantConfiguration',
      name: t('ASSISTANT_MGMT.TABS.ASSISTANT_CONFIGURATION'),
    },
    {
      key: 'knowledge',
      name: t('ASSISTANT_MGMT.TABS.ASSISTANT_KNOWLEDGE'),
    },
    {
      key: 'instructions',
      name: t('ASSISTANT_MGMT.TABS.INSTRUCTIONS'),
    },
    {
      key: 'procedures',
      name: t('ASSISTANT_MGMT.TABS.PROCEDURES'),
    },
  ];

  return configuredTabs;
});

const selectedTabKey = computed(() => tabs.value[selectedTabIndex.value]?.key);

const currentAssistantId = computed(() => route.params.assistantId);
const assistant = useFunctionGetter(
  'assistants/getAssistant',
  currentAssistantId
);
const hasActiveAssistant = computed(
  () => assistant.value && Object.keys(assistant.value).length
);

const assistantInbox = computed(() => {
  // Ensure we have both assistant and inboxes data loaded
  if (!hasActiveAssistant.value || !inboxes.value.length) {
    return null;
  }

  const agentId = assistant.value.agent_id;
  if (!agentId) {
    return null;
  }

  return inboxes.value.find(
    inbox =>
      inbox?.agent_bot?.bot_config?.agent_id === agentId &&
      inbox.channel_type === 'Channel::WebWidget'
  );
});

// Check if data is loaded and assistant has webchannel for tester
const isDataLoaded = computed(() => {
  return !assistantUIFlags.value.isFetching && !inboxUIFlags.value.isFetching;
});

const hasWebchannel = computed(() => {
  if (!isDataLoaded.value || !hasActiveAssistant.value) {
    return false;
  }
  return assistantInbox.value?.channel_type === 'Channel::WebWidget';
});

function onTabChange(index) {
  selectedTabIndex.value = index;
}

onMounted(async () => {
  const promises = [];

  if (!assistants.value.length) {
    promises.push(store.dispatch('assistants/get'));
  }

  if (!inboxes.value.length) {
    promises.push(store.dispatch('inboxes/get'));
  }

  if (promises.length > 0) {
    await Promise.all(promises);
  }

  if (currentAssistantId.value) {
    await store.dispatch('assistants/show', currentAssistantId.value);
  }
});
</script>

<template>
  <div
    class="flex-grow flex-shrink w-full min-w-0 pl-0 pr-0 overflow-auto settings"
    data-testid="assistant-settings-page"
  >
    <SettingIntroBanner
      :header-image="assistant.avatar_url"
      :header-title="assistant.name"
      max-width="max-w-8xl"
    >
      <woot-tabs
        class="settings--tabs"
        data-testid="assistant-settings-tabs"
        :index="selectedTabIndex"
        :border="false"
        @change="onTabChange"
      >
        <woot-tabs-item
          v-for="(tab, index) in tabs"
          :key="tab.key"
          :index="index"
          :name="tab.name"
          :show-badge="false"
        />
      </woot-tabs>
    </SettingIntroBanner>

    <div v-if="hasActiveAssistant" class="flex h-full gap-6">
      <div class="flex-1 min-w-0">
        <div v-if="selectedTabKey === 'assistantConfiguration'">
          <AssistantConfiguration :assistant-id="assistant.id" />
        </div>
        <div v-if="selectedTabKey === 'knowledge'">
          <KnowledgePage :assistant-id="assistant.id" />
        </div>
        <div v-if="selectedTabKey === 'instructions'" class="mx-8">
          <InstructionsPage :assistant-id="assistant.id" />
        </div>
        <div v-if="selectedTabKey === 'procedures'" class="mx-8">
          <ProcedureEditor :assistant-id="assistant.id" />
        </div>
      </div>

      <!-- <div
        v-if="hasWebchannel"
        class="hidden xl:block xl:w-[32rem] h-[calc(90vh-5rem)]"
      >
        <AssistantTester :assistant="assistant" :inbox="assistantInbox" />
      </div> -->
    </div>

    <!-- <AssistantTesterMobile
      v-if="hasWebchannel"
      class="xl:hidden"
      :assistant="assistant"
      :inbox="assistantInbox"
    /> -->
  </div>
</template>

<style scoped lang="scss">
.settings--tabs {
  ::v-deep .tabs {
    padding: 0;
  }
}
</style>
